import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "../../../../utils/apparifySwal";
import { Plus, Search, Tag, Tags } from "lucide-react";
import ShopSelect from "../../../../components/ShopSelect";
import { Button } from "../../../../components/ui";

import { BtnPrimary, BtnSecondary, FormModal } from "../../../components/erp";
import { businessProductsAPI } from "../../../services/businessProductsService";
import { businessSwalBase } from "../../../utils/businessSwal";

function isBlank(v) {
  return v == null || String(v).trim() === "";
}

function normalizeList(payload) {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function initialCategoryForm() {
  return { name: "", position: "0", description: "" };
}

function initialTagForm() {
  return { name: "", description: "" };
}

export default function StepBasicInfoCatalog({ state, setState, showRequiredErrors = false }) {
  const basic = state?.basic || {};
  const showNameError = showRequiredErrors && isBlank(basic.name);
  const showCategoryError = showRequiredErrors && isBlank(basic.category);
  const selectedTagIds = useMemo(
    () => (Array.isArray(basic?.tags) ? basic.tags.map(String) : []),
    [basic?.tags],
  );

  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [categoryQuery, setCategoryQuery] = useState("");
  const [tagQuery, setTagQuery] = useState("");
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState(initialCategoryForm());
  const [tagForm, setTagForm] = useState(initialTagForm());
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingTag, setSavingTag] = useState(false);

  const swalBase = useMemo(
    () => ({
      ...businessSwalBase,
      target: "#business-swal-root",
    }),
    [],
  );

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const [categoriesRes, tagsRes] = await Promise.all([
        businessProductsAPI.getCategories(),
        businessProductsAPI.getTags(),
      ]);
      setCategories(normalizeList(categoriesRes));
      setTags(normalizeList(tagsRes));
    } catch (err) {
      console.error("[StepBasicInfoCatalog] Failed to load category/tag catalog:", err);
      setCategories([]);
      setTags([]);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const setBasic = (patch) =>
    setState((prev) => ({ ...prev, basic: { ...(prev.basic || {}), ...patch } }));

  const selectedCategory = useMemo(
    () => categories.find((category) => String(category.id) === String(basic?.category || "")) || null,
    [categories, basic?.category],
  );

  const filteredCategories = useMemo(() => {
    const needle = String(categoryQuery || "").trim().toLowerCase();
    const base = needle
      ? categories.filter((category) => String(category.name || "").toLowerCase().includes(needle))
      : [...categories];
    if (selectedCategory && !base.some((category) => String(category.id) === String(selectedCategory.id))) {
      base.unshift(selectedCategory);
    }
    return base;
  }, [categories, categoryQuery, selectedCategory]);

  const selectedTags = useMemo(() => {
    const chosen = new Set(selectedTagIds);
    return tags.filter((tag) => chosen.has(String(tag.id)));
  }, [selectedTagIds, tags]);

  const filteredTags = useMemo(() => {
    const needle = String(tagQuery || "").trim().toLowerCase();
    if (!needle) return tags;
    return tags.filter((tag) => {
      const haystack = `${tag.name || ""} ${tag.description || ""}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [tagQuery, tags]);

  const toggleTag = (tagId) => {
    const nextTagId = String(tagId);
    const next = selectedTagIds.includes(nextTagId)
      ? selectedTagIds.filter((id) => id !== nextTagId)
      : [...selectedTagIds, nextTagId];
    setBasic({ tags: next });
  };

  const inputBase =
    "mt-1 w-full rounded-xl border bg-white px-3 py-3 text-sm outline-none " +
    "focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200 " +
    "dark:bg-[#0f0f10] dark:focus:border-zinc-600 dark:focus:ring-zinc-800";

  const createCategoryInline = async () => {
    const name = String(categoryForm.name || "").trim();
    const description = String(categoryForm.description || "").trim();
    const position = Number(categoryForm.position || 0);

    if (!name) {
      await Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Category name required",
        text: "Enter a category name before saving.",
      });
      return;
    }
    if (!Number.isInteger(position) || position < 0) {
      await Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Invalid position",
        text: "Category position must be a whole number at or above zero.",
      });
      return;
    }

    setSavingCategory(true);
    try {
      const res = await businessProductsAPI.createCategory({
        name,
        description,
        position,
        source: "product_form",
      });
      const created = res?.data || res;
      setCategoryModalOpen(false);
      setCategoryForm(initialCategoryForm());
      await loadCatalog();
      if (created?.id) {
        setBasic({ category: String(created.id) });
      }
      setCategoryQuery("");
    } catch (err) {
      await Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Create category failed",
        text: err?.message || "Unable to create the product category.",
      });
    } finally {
      setSavingCategory(false);
    }
  };

  const createTagInline = async () => {
    const name = String(tagForm.name || "").trim();
    const description = String(tagForm.description || "").trim();

    if (!name) {
      await Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Tag name required",
        text: "Enter a tag name before saving.",
      });
      return;
    }

    setSavingTag(true);
    try {
      const res = await businessProductsAPI.createTag({
        name,
        description,
        source: "product_form",
      });
      const created = res?.data || res;
      setTagModalOpen(false);
      setTagForm(initialTagForm());
      await loadCatalog();
      if (created?.id) {
        setBasic({ tags: [...selectedTagIds, String(created.id)] });
      }
      setTagQuery("");
    } catch (err) {
      await Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Create tag failed",
        text: err?.message || "Unable to create the product tag.",
      });
    } finally {
      setSavingTag(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="add-product-name" className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">
          Product Name <span className="text-red-500">*</span>
        </label>
        <input
          id="add-product-name"
          value={basic?.name || ""}
          onChange={(e) => setBasic({ name: e.target.value })}
          placeholder="e.g. Oversized Tee"
          aria-invalid={showNameError || undefined}
          aria-describedby={showNameError ? "add-product-name-error" : undefined}
          className={
            inputBase +
            " " +
            (showNameError
              ? "border-red-200 bg-red-50 text-red-950 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-100"
              : "border-zinc-200 text-zinc-900 dark:border-zinc-800 dark:text-zinc-100")
          }
        />
        {showNameError && (
          <div id="add-product-name-error" className="mt-1 text-[11px] text-red-600 dark:text-red-300" role="alert">
            Product name is required.
          </div>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <label className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                Category <span className="text-red-500">*</span>
              </label>
              <p className="mt-0.5 text-xs leading-5 text-zinc-600 dark:text-zinc-400">
                Choose one primary category for this product.
              </p>
            </div>
            <Button size="sm" icon={Plus} onClick={() => setCategoryModalOpen(true)}>
              New Category
            </Button>
          </div>

          <div className="mt-3 relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              value={categoryQuery}
              onChange={(e) => setCategoryQuery(e.target.value)}
              placeholder="Search categories..."
              className="w-full rounded-xl border border-zinc-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200 dark:border-zinc-800 dark:bg-[#0f0f10] dark:text-zinc-100 dark:focus:border-zinc-600 dark:focus:ring-zinc-800"
            />
          </div>

          <ShopSelect
            value={basic?.category || ""}
            onValueChange={(value) => setBasic({ category: value })}
            options={filteredCategories.map((category) => ({
              value: String(category.id),
              label: `${category.name}${category.is_protected_default ? " - Default" : " - Custom"}`,
            }))}
            placeholder={catalogLoading ? "Loading categories..." : "Choose a category"}
            disabled={catalogLoading}
            invalid={showCategoryError}
            ariaLabel="Product category"
            align="start"
            minPanelWidth={280}
            triggerClassName="mt-3 min-h-11 w-full rounded-xl px-3 py-2.5 text-sm"
            panelClassName="py-1"
            optionClassName="text-sm"
          />

          {selectedCategory ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-zinc-500 dark:text-zinc-400">
              <span className="inline-flex rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 dark:border-zinc-700 dark:bg-zinc-900/60">
                {selectedCategory.is_protected_default ? "Default system category" : "Business custom category"}
              </span>
              <span>{selectedCategory.product_count || 0} products currently use this category</span>
            </div>
          ) : null}

          {showCategoryError && (
            <div className="mt-1 text-[11px] text-red-600 dark:text-red-300" role="alert">
              Category is required.
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <label className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                Product Tags
              </label>
              <p className="mt-0.5 text-xs leading-5 text-zinc-600 dark:text-zinc-400">
                Add optional tags to describe the product.
              </p>
            </div>
            <Button size="sm" icon={Plus} onClick={() => setTagModalOpen(true)}>
              New Tag
            </Button>
          </div>

          <div className="mt-3 relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              value={tagQuery}
              onChange={(e) => setTagQuery(e.target.value)}
              placeholder="Search tags..."
              className="w-full rounded-xl border border-zinc-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200 dark:border-zinc-800 dark:bg-[#0f0f10] dark:text-zinc-100 dark:focus:border-zinc-600 dark:focus:ring-zinc-800"
            />
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {selectedTags.length ? selectedTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTag(tag.id)}
                className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs font-medium text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900/60 dark:text-zinc-200"
              >
                <Tag className="h-3 w-3" />
                {tag.name}
              </button>
            )) : (
              <span className="text-xs text-zinc-500 dark:text-zinc-400">
                No product tags selected yet.
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {catalogLoading ? (
              <span className="text-xs text-zinc-500 dark:text-zinc-400">Loading tags...</span>
            ) : !filteredTags.length ? (
              <span className="text-xs text-zinc-500 dark:text-zinc-400">No reusable tags matched your search.</span>
            ) : (
              filteredTags.map((tag) => {
                const active = selectedTagIds.includes(String(tag.id));
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={[
                      "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition",
                      active
                        ? "border-zinc-900 bg-zinc-900 text-white dark:border-zinc-100 dark:bg-zinc-100 dark:text-zinc-900"
                        : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-[#0f0f10] dark:text-zinc-200 dark:hover:bg-zinc-900/60",
                    ].join(" ")}
                  >
                    <Tags className="h-3 w-3" />
                    {tag.name}
                    <span className="text-[10px] opacity-70">({tag.product_count || 0})</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">
          Description
        </label>
        <textarea
          rows={6}
          value={basic?.description || ""}
          onChange={(e) => setBasic({ description: e.target.value })}
          placeholder="Minimal, clean copy... sizing notes help a lot."
          className={inputBase + " border-zinc-200 text-zinc-900 dark:border-zinc-800 dark:text-zinc-100"}
        />
        <div className="mt-1 text-[11px] text-zinc-500 dark:text-zinc-400">
          Tip: Add fit, fabric, and care instructions to reduce returns.
        </div>
      </div>

      <FormModal
        open={categoryModalOpen}
        onClose={() => {
          setCategoryModalOpen(false);
          setCategoryForm(initialCategoryForm());
        }}
        title="New Product Category"
        size="sm"
        footer={(
          <>
            <BtnSecondary
              onClick={() => {
                setCategoryModalOpen(false);
                setCategoryForm(initialCategoryForm());
              }}
            >
              Cancel
            </BtnSecondary>
            <BtnPrimary onClick={createCategoryInline} disabled={savingCategory}>
              {savingCategory ? "Saving..." : "Create Category"}
            </BtnPrimary>
          </>
        )}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              Category Name
            </label>
            <input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none dark:border-zinc-800 dark:bg-[#0f0f10] dark:text-zinc-100"
              maxLength={100}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              Display Position
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={categoryForm.position}
              onChange={(e) => setCategoryForm((prev) => ({ ...prev, position: e.target.value }))}
              className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none dark:border-zinc-800 dark:bg-[#0f0f10] dark:text-zinc-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              Description
            </label>
            <textarea
              rows={3}
              value={categoryForm.description}
              onChange={(e) => setCategoryForm((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none dark:border-zinc-800 dark:bg-[#0f0f10] dark:text-zinc-100"
              maxLength={500}
            />
          </div>
        </div>
      </FormModal>

      <FormModal
        open={tagModalOpen}
        onClose={() => {
          setTagModalOpen(false);
          setTagForm(initialTagForm());
        }}
        title="New Product Tag"
        size="sm"
        footer={(
          <>
            <BtnSecondary
              onClick={() => {
                setTagModalOpen(false);
                setTagForm(initialTagForm());
              }}
            >
              Cancel
            </BtnSecondary>
            <BtnPrimary onClick={createTagInline} disabled={savingTag}>
              {savingTag ? "Saving..." : "Create Tag"}
            </BtnPrimary>
          </>
        )}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              Tag Name
            </label>
            <input
              value={tagForm.name}
              onChange={(e) => setTagForm((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none dark:border-zinc-800 dark:bg-[#0f0f10] dark:text-zinc-100"
              maxLength={80}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              Description
            </label>
            <textarea
              rows={3}
              value={tagForm.description}
              onChange={(e) => setTagForm((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none dark:border-zinc-800 dark:bg-[#0f0f10] dark:text-zinc-100"
              maxLength={255}
            />
          </div>
        </div>
      </FormModal>
    </div>
  );
}
